import { c as _c } from "react/compiler-runtime";
import classNames from 'classnames';
import type { AriaAttributes, ElementType, HTMLAttributes, ReactNode } from 'react';
import styles from './ContentBlock.module.scss';
export type ContentBlockAlign = 'start' | 'center';
export type ContentBlockAs = 'div' | 'section' | 'article';
export type ContentBlockPadding = 'none' | 'sm' | 'md' | 'lg' | 'xl';
export type ContentBlockRhythm = 'default' | 'compact' | 'callout';
export type ContentBlockSurface = 'transparent' | 'subtle' | 'dark' | 'brand';
export type ContentBlockTone = 'default' | 'inverse';
export interface ContentBlockProps extends AriaAttributes, Pick<HTMLAttributes<HTMLElement>, 'id' | 'role'> {
  align?: ContentBlockAlign;
  as?: ContentBlockAs;
  children: ReactNode;
  fullHeight?: boolean;
  padding?: ContentBlockPadding;
  rhythm?: ContentBlockRhythm;
  /**
   * `subtle`, `dark` and `brand` draw the callout: the redesign card's flat
   * panel at radius 24 (`dark` is the inverse #080808 callout); `transparent`
   * (default) draws none and only sets the text rhythm.
   */
  surface?: ContentBlockSurface;
  tone?: ContentBlockTone;
}
function ContentBlock(t0) {
  const $ = _c(23);
  let children;
  let semanticProps;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let tone;
  if ($[0] !== t0) {
    ({
      align: t1,
      as: t2,
      children,
      fullHeight: t3,
      padding: t4,
      rhythm: t5,
      surface: t6,
      tone,
      ...semanticProps
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = semanticProps;
    $[3] = t1;
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
    $[7] = t5;
    $[8] = t6;
    $[9] = tone;
  } else {
    children = $[1];
    semanticProps = $[2];
    t1 = $[3];
    t2 = $[4];
    t3 = $[5];
    t4 = $[6];
    t5 = $[7];
    t6 = $[8];
    tone = $[9];
  }
  const align = t1 === undefined ? "start" : t1;
  const as = t2 === undefined ? "div" : t2;
  const fullHeight = t3 === undefined ? false : t3;
  const padding = t4 === undefined ? "none" : t4;
  const rhythm = t5 === undefined ? "default" : t5;
  const surface = t6 === undefined ? "transparent" : t6;
  const Component = as as ElementType;
  const resolvedTone = tone ?? (surface === "dark" || surface === "brand" ? "inverse" : "default");
  const surfaceTone = surface === "dark" || surface === "brand" ? surface : undefined;
  const t7 = styles[`helios-content-block--align-${align}`];
  const t8 = styles[`helios-content-block--padding-${padding}`];
  const t9 = styles[`helios-content-block--rhythm-${rhythm}`];
  const t10 = styles[`helios-content-block--surface-${surface}`];
  const t11 = styles[`helios-content-block--tone-${resolvedTone}`];
  const t12 = fullHeight && styles["helios-content-block--full-height"];
  let t13;
  if ($[10] !== t10 || $[11] !== t11 || $[12] !== t12 || $[13] !== t7 || $[14] !== t8 || $[15] !== t9) {
    t13 = classNames(styles["helios-content-block"], t7, t8, t9, t10, t11, t12);
    $[10] = t10;
    $[11] = t11;
    $[12] = t12;
    $[13] = t7;
    $[14] = t8;
    $[15] = t9;
    $[16] = t13;
  } else {
    t13 = $[16];
  }
  let t14;
  if ($[17] !== Component || $[18] !== children || $[19] !== semanticProps || $[20] !== surfaceTone || $[21] !== t13) {
    t14 = <Component {...semanticProps} data-helios-surface={surfaceTone} className={t13}>{children}</Component>;
    $[17] = Component;
    $[18] = children;
    $[19] = semanticProps;
    $[20] = surfaceTone;
    $[21] = t13;
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  return t14;
}
export default ContentBlock;